import { Injectable } from '@angular/core';
import { ColumnFilters } from '../models/ui/table-column.model';

/** Persisted listing page/size/column filters for round-trips to detail/edit forms. */
export interface ListCriteria {
  page: number;
  size: number;
  columnFilters?: ColumnFilters;
}

/**
 * Remembers BaseListComponent query criteria per list path so forms can
 * navigate back without resetting filters/pagination.
 */
@Injectable({ providedIn: 'root' })
export class ListCriteriaService {
  private readonly prefix = 'rr.listCriteria:';
  private readonly memory = new Map<string, ListCriteria>();

  save(listPath: string, criteria: ListCriteria): void {
    const key = this.key(listPath);
    const payload: ListCriteria = {
      page: criteria.page,
      size: criteria.size,
    };
    const filters = this.sanitizeFilters(criteria.columnFilters);
    if (Object.keys(filters).length > 0) {
      payload.columnFilters = filters;
    }
    this.memory.set(key, payload);
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // Ignore quota / private-mode failures; in-memory still works for the session.
    }
  }

  /** Query params suitable for router.navigate back to the list. */
  getQueryParams(listPath: string): Record<string, string | number> | undefined {
    const key = this.key(listPath);
    const criteria = this.memory.get(key) ?? this.readSession(key);
    if (!criteria) {
      return undefined;
    }
    const queryParams: Record<string, string | number> = {
      page: criteria.page,
      size: criteria.size,
      ...this.sanitizeFilters(criteria.columnFilters),
    };
    return queryParams;
  }

  private sanitizeFilters(filters?: ColumnFilters): ColumnFilters {
    if (!filters) {
      return {};
    }
    const out: ColumnFilters = {};
    for (const [k, v] of Object.entries(filters)) {
      if (typeof v === 'string' && v.trim()) {
        out[k] = v.trim();
      }
    }
    return out;
  }

  private readSession(key: string): ListCriteria | undefined {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) {
        return undefined;
      }
      const parsed = JSON.parse(raw) as ListCriteria & { keywords?: string };
      if (typeof parsed?.page !== 'number' || typeof parsed?.size !== 'number') {
        return undefined;
      }
      // Migrate legacy keywords-only criteria (discard; no 1:1 column mapping).
      if (!parsed.columnFilters && parsed.keywords) {
        parsed.columnFilters = undefined;
      }
      this.memory.set(key, parsed);
      return parsed;
    } catch {
      return undefined;
    }
  }

  private key(listPath: string): string {
    const path = listPath.startsWith('/') ? listPath : `/${listPath}`;
    // Strip trailing slash and any accidental query fragment
    return `${this.prefix}${path.split('?')[0].replace(/\/$/, '') || '/'}`;
  }
}
